import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "@shared/components/Molecules/Modal/Modal.component";
import { SvgIcon } from "@shared/components/Atoms/SvgIcon/SvgIcon";
import Button from "@shared/components/Atoms/Button/Button";
import { FavoriteList } from "@shared/helpers/favoritesHelper";
import { getRoutePathByPageType } from "@shared/helpers/findPathForSystemSite";
import { useTranslations } from "@shared/context/useTranslations";
import { IconNames } from "@shared/enums/icons.enums";
import { useFavoriteManagement } from "@shared/hooks/useFavoriteManagement";
import S from "./FavoritesModal.module.scss";

type FavoriteManagement = ReturnType<typeof useFavoriteManagement>;

interface FavoritesModalProps {
    controller: FavoriteManagement;
}

export const FavoritesModal = ({ controller }: FavoritesModalProps) => {
    const {
        isModalOpen,
        setIsModalOpen,
        selectedLists,
        setSelectedLists,
        handleSaveFavorites,
        getModalTitle,
        getModalDescription,
        favorites,
    } = controller;

    const navigate = useNavigate();
    const { getTranslation } = useTranslations();
    const favoritesPath = getRoutePathByPageType("Favorites") || "/default-favorites-path";
    const [pendingSave, setPendingSave] = useState(false);
    const [listKey, setListKey] = useState(0);

    useEffect(() => {
        if (pendingSave) {
            handleSaveFavorites(false);
            setTimeout(() => {
                setPendingSave(false);
            }, 50);
        }
        setListKey((k) => k + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingSave, selectedLists]);

    return (
        <Modal
            isOpen={isModalOpen}
            onRequestClose={() => setIsModalOpen(false)}
            title={getModalTitle()}
            description={getModalDescription()}
        >
            {favorites && favorites.length > 0 ? (
                favorites.map((list: FavoriteList) => (
                    <div key={list.id} className={S.favoriteListItem}>
                        <div className={S.favoriteToggleWrapper}>
                            <Button
                                size="fitIcon"
                                className={S.favoriteToggle}
                                onClick={() => {
                                    setSelectedLists((prev: Record<string, boolean>) => ({
                                        ...prev,
                                        [list.id]: !prev[list.id],
                                    }));
                                    setPendingSave(true);
                                }}
                            >
                                <SvgIcon
                                    // Force icon re-render via key update due to Safari SVG issue
                                    key={isModalOpen ? `${list.id}${listKey}` : list.id}
                                    wrapperStyle={
                                        S[
                                            pendingSave
                                                ? selectedLists[list.id]
                                                    ? "adding"
                                                    : "removing"
                                                : ""
                                        ]
                                    }
                                    iconName={
                                        selectedLists[list.id]
                                            ? IconNames.Heartfull
                                            : IconNames.Heart
                                    }
                                />
                            </Button>
                        </div>
                        <div className={S.infoBox}>
                            <span className={S.favoriteListName}>{list.name}</span>
                            <span className={S.favoriteListCount}>
                                {list.items.length}{" "}
                                {list.items.length > 1
                                    ? getTranslation("Favorites.itemNumbers")
                                    : getTranslation("Favorites.itemNumber")}
                            </span>
                        </div>
                        <Button
                            onClick={() => {
                                setIsModalOpen(false);
                                navigate(`${favoritesPath}?listId=${list.id}`);
                            }}
                            className={S.goToListBtn}
                            variant="transparent"
                            size="small"
                            iconName={IconNames.Backicon}
                        >
                            {getTranslation("button.toList")}
                        </Button>
                    </div>
                ))
            ) : (
                <div className={S.noFavoritesMessage}>
                    <Button
                        round
                        onClick={() => {
                            setIsModalOpen(false);
                            navigate(`${favoritesPath}`);
                        }}
                        iconName={IconNames.Backicon}
                    >
                        {getTranslation("button.toFavorites")}
                    </Button>
                    <Button round variant="transparent" onClick={() => setIsModalOpen(false)}>
                        {getTranslation("button.cancel")}
                    </Button>
                </div>
            )}
            <div className={S.modalFooter}>
                {favorites && favorites.length > 0 && (
                    <Button onClick={handleSaveFavorites}>
                        {getTranslation("button.save")}
                    </Button>
                )}
                <Button variant="transparent" onClick={() => setIsModalOpen(false)}>
                    {getTranslation("button.cancel")}
                </Button>
            </div>
        </Modal>
    );
};
